import React, { useState, useEffect } from "react";
import { Outlet, NavLink } from "react-router-dom";
import { useAuth } from "../auth/AuthContext";
import logo from "../assets/logo2.png";
import { LogOut, User } from "lucide-react";

function avatarInitial(email?: string, name?: string): string {
  if (name && name.trim()) return name.trim().charAt(0).toUpperCase();
  if (email && email.trim()) return email.trim().charAt(0).toUpperCase();
  return "?";
}

const AVATAR_BG: Record<string, string> = {
  admin: "rgba(239,68,68,0.2)",
  analyst: "rgba(168,85,247,0.2)",
  client: "rgba(0,102,224,0.2)",
};

const AVATAR_COLOR: Record<string, string> = {
  admin: "#ef4444",
  analyst: "#a855f7",
  client: "#818cf8",
};

export default function SubscriptionLayout() {
  const { user, logout } = useAuth();
  const [theme, setTheme] = useState<"dark" | "light">(() => {
    return (localStorage.getItem("admin-dashboard-theme") as "dark" | "light") || "dark";
  });
  const [showDropdown, setShowDropdown] = useState(false);

  const toggleTheme = () => {
    const newTheme = theme === "dark" ? "light" : "dark";
    setTheme(newTheme);
    localStorage.setItem("admin-dashboard-theme", newTheme);
  };

  useEffect(() => {
    if (theme === "light") {
      document.body.classList.add("light-theme");
      document.documentElement.classList.remove("dark");
    } else {
      document.body.classList.remove("light-theme");
      document.documentElement.classList.add("dark");
    }
  }, [theme]);

  const role = user?.role ?? "client";
  const initial = avatarInitial(user?.email, user?.name);
  const avatarBg = AVATAR_BG[role] ?? AVATAR_BG.client;
  const avatarColor = AVATAR_COLOR[role] ?? AVATAR_COLOR.client;

  return (
    <div className={`h-screen w-full flex flex-col font-sans transition-colors duration-200 ${theme === "light" ? "bg-[#f4f6fa] text-slate-900" : "bg-[#080b12] text-slate-100"}`}>
      {/* Minimal Header */}
      <header className={`h-16 px-4 md:px-6 flex items-center justify-between border-b ${theme === "light" ? "border-slate-200/60 bg-white/80" : "border-slate-800/60 bg-[#080b12]/80"} backdrop-blur-md sticky top-0 z-50 shrink-0`}>
        <NavLink to="/dashboard" className="flex items-center gap-2 focus:outline-none focus:ring-2 focus:ring-indigo-500 rounded" aria-label="Go to Dashboard">
          <img src={logo} alt="Threatactix Logo" className={`h-8 w-auto object-contain ${theme === "light" ? "invert hue-rotate-180" : ""}`} />
        </NavLink>

        <div className="relative">
          <button 
            onClick={() => setShowDropdown(!showDropdown)}
            className="flex items-center justify-center h-9 w-9 md:h-10 md:w-10 rounded-full focus:outline-none focus:ring-2 focus:ring-indigo-500 transition-transform hover:scale-105"
            style={{ backgroundColor: avatarBg, color: avatarColor }}
            aria-label="User menu"
            aria-expanded={showDropdown}
          >
            {/* If user had an image, we'd render it here as <img src={user.image} ... /> */}
            <span className="font-semibold text-sm md:text-base">{initial}</span>
          </button>

          {showDropdown && (
            <div className={`absolute right-0 mt-2 w-48 rounded-md shadow-lg py-1 ring-1 ring-black ring-opacity-5 ${theme === "light" ? "bg-white" : "bg-[#1e2330]"}`}>
              <NavLink 
                to="/profile" 
                className={`flex items-center gap-2 px-4 py-2 text-sm ${theme === "light" ? "text-slate-700 hover:bg-slate-100" : "text-slate-200 hover:bg-slate-700/50"}`}
                onClick={() => setShowDropdown(false)}
              >
                <User className="h-4 w-4" />
                Profile
              </NavLink>
              <button 
                onClick={() => {
                  setShowDropdown(false);
                  logout();
                }}
                className={`flex w-full items-center gap-2 px-4 py-2 text-sm ${theme === "light" ? "text-red-600 hover:bg-red-50" : "text-red-400 hover:bg-red-900/20"}`}
              >
                <LogOut className="h-4 w-4" />
                Logout
              </button>
            </div>
          )}
        </div>
      </header>

      {/* Main Content Area */}
      <main className="flex-1 w-full flex flex-col overflow-y-auto relative">
        <Outlet context={{ theme, toggleTheme }} />
      </main>
    </div>
  );
}
